import { motion, AnimatePresence, useMotionValue, Variants } from 'framer-motion';
import { X, Sun, Moon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { NavItem } from './PageHeader';
import { useTheme } from './ThemeContext';
import { Button } from './ui/button';

interface AnimatedMenuProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isDark: boolean;
  menuItems: NavItem[];
  scrollToSection: (id: string) => void;
  customActionMobile?: React.ReactNode;
}

export function AnimatedMenu({ isOpen, setIsOpen, isDark, menuItems, scrollToSection, customActionMobile }: AnimatedMenuProps) {
  const dragX = useMotionValue(0);
  const { toggleTheme } = useTheme();

  const handleDragEnd = (_event: any, info: any) => {
    if (info.offset.x < -100) {
      setIsOpen(false);
    }
    dragX.set(0);
  };

  const menuVariants: Variants = {
    closed: {
      x: '-100%',
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 30,
        mass: 0.8,
      },
    },
    open: {
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 30,
        mass: 0.8,
      },
    },
  };

  const itemVariants: Variants = {
    closed: { x: -50, opacity: 0 },
    open: (i: number) => ({
      x: 0,
      opacity: 1,
      transition: {
        delay: 0.1 + i * 0.08,
        type: 'spring',
        stiffness: 250,
        damping: 25,
      },
    }),
  };

  const overlayVariants: Variants = {
    closed: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
    open: {
      opacity: 1,
      transition: {
        duration: 0.4,
      },
    },
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={overlayVariants}
            initial="closed"
            animate="open"
            exit="closed"
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] md:hidden"
          />
        )}
      </AnimatePresence>

      <motion.nav
        variants={menuVariants}
        initial="closed"
        animate={isOpen ? 'open' : 'closed'}
        drag="x"
        dragConstraints={{ left: -320, right: 0 }}
        dragElastic={0.2}
        onDragEnd={handleDragEnd}
        style={{ x: dragX }}
        className={`fixed inset-y-0 left-0 h-[100dvh] w-[80vw] max-w-sm z-[70] shadow-2xl md:hidden flex flex-col ${isDark ? 'bg-zinc-950 border-r border-zinc-900' : 'bg-white border-r border-gray-200'
          }`}
      >
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          whileHover={{ scale: 1.1, rotate: 90 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsOpen(false)}
          className={`absolute top-6 right-6 p-2 rounded-full transition-colors ${isDark
            ? 'bg-zinc-800 text-white hover:bg-zinc-700'
            : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
            }`}
        >
          <X size={24} />
        </motion.button>

        {/* Drag Indicator removed */}

        <div className="p-8 pt-20 flex-1 overflow-y-auto h-full flex flex-col">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, type: 'spring', stiffness: 200 }}
            className="mb-12"
          >
            <h2
              className={`text-2xl font-medium ${isDark ? 'text-white/50' : 'text-gray-900'
                }`}
            >
              Quilt Wellness
            </h2>

          </motion.div>

          <ul className={`space-y-4 flex-1 ${menuItems.length === 0 ? 'hidden' : ''}`}>
            {menuItems.map((item, i) => (
              <motion.li
                key={item.label}
                custom={i}
                variants={itemVariants}
                initial="closed"
                animate={isOpen ? 'open' : 'closed'}
              >
                {item.href ? (
                  <Link
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className={`block p-4 rounded-xl transition-all ${isDark
                      ? 'hover:bg-zinc-900 text-gray-200'
                      : 'hover:bg-gray-100 text-gray-800'
                      }`}
                  >
                    <span className="flex items-center gap-3">
                      {item.selected && <span className={`w-2 h-2 rounded-full ${isDark ? 'bg-purple-500' : 'bg-blue-600'}`} />}
                      <span className={`text-lg font-medium ${item.selected ? 'underline underline-offset-4' : ''}`}>{item.label}</span>
                    </span>
                  </Link>
                ) : (
                  <button
                    onClick={() => {
                      if (item.id) scrollToSection(item.id);
                      setIsOpen(false);
                    }}
                    className={`w-full text-left p-4 rounded-xl transition-all ${isDark
                      ? 'hover:bg-zinc-900 text-gray-200'
                      : 'hover:bg-gray-100 text-gray-800'
                      }`}
                  >
                    <span className="flex items-center gap-3">
                      {item.selected && <span className={`w-2 h-2 rounded-full ${isDark ? 'bg-purple-500' : 'bg-blue-600'}`} />}
                      <span className={`text-lg font-medium ${item.selected ? 'underline underline-offset-4' : ''}`}>{item.label}</span>
                    </span>
                  </button>
                )}
              </motion.li>
            ))}
          </ul>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isOpen ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ delay: 0.3, type: 'tween', stiffness: 200 }}
            className={`${menuItems.length > 0 ? 'mt-8 pt-6 border-t' : ''} flex flex-col gap-4 text-lg ${isDark ? 'border-zinc-800' : 'border-gray-200'}`}
          >
            <Button
              variant="default"
              onClick={toggleTheme}
              className={`w-full justify-between h-auto px-4 py-3 rounded-3xl transition-all font-normal ${isDark
                ? "bg-zinc-800 hover:bg-zinc-900 text-gray-200"
                : "bg-gray-100 hover:bg-gray-100 text-gray-800"
                }`}
            >
              <span className="flex items-center gap-3">
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                <span className="text-lg font-medium">Dark theme</span>
              </span>
              <span className="text-lg font-medium opacity-60">
                {isDark ? 'On' : 'Off'}
              </span>
            </Button>
            {/* display:contents keeps the actions as direct flex children; any tap closes the drawer */}
            <div className="contents" onClick={() => setIsOpen(false)}>
              {customActionMobile}
            </div>
          </motion.div>

        </div>
      </motion.nav>
    </>
  );
}
